import { useCallback, useEffect, useState } from 'react'

import { PaymentActionType, useFinancePay } from '@/api/models/finance'
import { isUrlAllowed } from '@/utils/url'

import { BalanceInvoiceMap } from '../BillsPayDrawer.types'
import { AcquiringFormData } from '../model/schemas'

type Props = {
  invoice: BalanceInvoiceMap
  invoiceIds: string[]
  receiptMethod: string
  opened?: boolean
}

export const useBillsFinancePay = ({
  invoice,
  invoiceIds,
  receiptMethod,
  opened
}: Props) => {
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { mutateAsync: makePayment, isPending: isPaymentPending } =
    useFinancePay()

  useEffect(() => {
    if (!opened) {
      setIsRedirecting(false)
      return
    }

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsRedirecting(false)
      }
    }

    window.addEventListener('pageshow', handlePageShow)

    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [opened])

  const handleMakePayment = useCallback(
    async ({
      amount,
      phone,
      email,
      cardId
    }: AcquiringFormData & { cardId?: string }) => {
      if (!invoice.merchId) {
        return
      }

      const { data } = await makePayment({
        invoiceIds,
        amount: Number(amount) || 0,
        merchId: invoice.merchId,
        cardId: cardId || undefined,
        phoneNumber: receiptMethod === 'phone' ? phone : null,
        email: receiptMethod === 'email' ? email : null,
        successReturnUrl: `${location.origin}/invoices`,
        errorReturnUrl: `${location.origin}/invoices`,
        paymentActionType: invoice.id
          ? PaymentActionType.Single
          : PaymentActionType.Multiply
      })

      if (data?.url && isUrlAllowed(data?.url)) {
        setIsRedirecting(true)
        window.location.href = `${data.url}?${data.body}`
      }
    },
    [invoice, invoiceIds, makePayment, receiptMethod]
  )

  return {
    isPaymentPending: isPaymentPending || isRedirecting,
    makePayment: handleMakePayment
  }
}
